import type { MessageKey } from './de.js'

/**
 * The worker app in the languages the staff actually speak. Typed as a full
 * record, so a key added to `de` without translations fails the build rather
 * than showing German to someone who asked for Polish.
 *
 * Machine-assisted and checked for tone; worth a read by a native speaker from
 * the team before go-live.
 */
type Catalogue = Record<MessageKey, string>

export const tr: Catalogue = {
  'app.title': 'Görevlerim',
  'app.tasks': 'Görevler',
  'app.stats': 'Rakamlarım',
  'app.back': 'Geri',
  'nav.today': 'Bugün',
  'nav.plan': 'Plan',
  'nav.report': 'Bildir',
  'nav.stats': 'Rakamlar',
  'nav.tour': 'Tur',
  'day.today': 'Bugün',
  'day.tomorrow': 'Yarın',
  'day.yesterday': 'Dün',
  'plan.title': 'Önümüzdeki günler',
  'plan.subtitle': 'Önümüzdeki iki haftada yapılacaklar',
  'plan.nothing_day': 'Bu gün için bir şey planlanmadı.',
  'plan.nothing': 'Önümüzdeki günlerde yapılacak bir şey yok.',
  'plan.tasks_one': '1 görev',
  'plan.tasks_many': '{count} görev',
  'plan.all_done': 'hepsi tamam',
  'plan.open_count': '{count} açık',
  'plan.later': 'Daha sonra',
  'sync.offline': 'İnternet yok, girdileriniz kaydediliyor',
  'sync.pending_one': '1 görev gönderiliyor',
  'sync.pending_many': '{count} görev gönderiliyor',
  'sync.syncing': 'Gönderiliyor …',
  'sync.done': 'Hepsi gönderildi',
  'today.overdue': 'Son günlerden kalanlar',
  'today.today': 'Bugün',
  'today.tomorrow': 'Yarın',
  'today.done': 'Bugün tamamlananlar',
  'today.empty': 'Bugün için bir şey planlanmadı.',
  'today.all_done': 'Bugünkü her şey tamamlandı.',
  'today.empty_hint': 'Daha sonra tekrar bakın.',
  'today.loading': 'Görevler yükleniyor …',
  'today.load_failed': 'Görevler yüklenemedi. İnternet gelince burada görünecekler.',
  'progress.all_done': 'Hepsi tamam. Teşekkürler!',
  'progress.remaining': '{count} görev kaldı',
  'progress.none_left': 'Bugün için açık bir şey kalmadı.',
  'progress.done_of': 'Bugünkü {total} görevden {done} tanesi bitti.',
  'card.until': 'saat {time} kadar',
  'card.photo_after': 'Sonra fotoğrafı',
  'card.photo_both': 'Önce ve sonra fotoğrafı',
  'card.done': 'tamamlandı',
  'card.sending': 'gönderiliyor',
  'group.open': '{count} açık',
  'group.finished': 'bitti',
  'group.route': 'Yol tarifi',
  'task.gone': 'Bu görev artık listenizde değil.',
  'task.was_due': 'Tarihi geçti: {when}',
  'task.not_due_yet': 'Bu görevin tarihi {date}. Yine de şimdiden tamamlandı olarak işaretleyebilirsiniz.',
  'task.done_at': '{date} tarihinde tamamlandı',
  'task.access': 'Giriş',
  'task.photo_before': 'Önce fotoğrafı',
  'task.photo_after': 'Sonra fotoğrafı',
  'task.photo_take': 'Fotoğraf çek',
  'task.photo_retake': 'Yeniden çek',
  'task.photo_other': 'Diğer fotoğraflar',
  'task.photo_optional': '(isteğe bağlı)',
  'task.photo_more': 'Bir fotoğraf daha',
  'task.photo_remove': 'Fotoğrafı kaldır',
  'task.photo_processing': 'Fotoğraf küçültülüyor …',
  'task.photo_required_after': 'Bu görev için iş bittikten sonra bir fotoğraf gerekiyor.',
  'task.photo_required_both': 'Bu görev için önce ve sonra birer fotoğraf gerekiyor.',
  'task.what_done': 'Ne yapıldı?',
  'task.option_required': 'Lütfen ne yapıldığını seçin.',
  'task.note': 'Not (isteğe bağlı)',
  'task.note_placeholder': 'örn. özel durumlar, açıklamalar',
  'task.complete': 'Tamamlandı',
  'task.completing': 'Kaydediliyor …',
  'task.completed_at': '{date} saat {time} tamamlandı',
  'task.queued': 'Kaydedildi. İnternet gelince gönderilecek.',
  'task.report_issue': 'Sorun bildir',
  'task.saved': 'Tamamlandı. Teşekkürler!',
  'card.team': 'Grup {name}',
  'task.team_hint': 'Grup görevi: İlk işaretleyen, görevi tamamlamış sayılır.',
  'task.already_done': '{name} bu görevi zaten tamamladı.',
  'task.already_done_anon': 'Bu görev zaten tamamlandı.',
  'task.reason_label': 'Gerekçe (zorunlu)',
  'task.defect_label': 'Bir kusur gördünüz mü? (isteğe bağlı)',
  'task.defect_placeholder': 'örn. güvercin yuvası, açık elektrik, duman dedektörü ötüyor',
  'task.reason_placeholder': 'Ne oldu? Örn. anahtar yok, kimse yoktu',
  'task.reason_required': 'Lütfen kısaca nedenini yazın.',
  'task.measurement_placeholder': 'Değeri girin',
  'task.measurement_required': 'Lütfen önce ölçüm değerini girin.',
  'task.signature': 'Yerinde imza',
  'task.signature_optional': 'Yerinde imza (isteğe bağlı)',
  'task.signature_hint': 'Parmakla imzalatın.',
  'task.signature_clear': 'Yeniden',
  'task.signed_by': 'İmzalayan kişinin adı',
  'task.signature_required': 'Bu görev için yerinde imza gerekiyor.',
  'stats.location': 'Tamamlarken konum',
  'stats.location_text': 'İşletmeniz konum kanıtını açtı. Onay verirseniz, uygulama bir görevi tamamladığınızda konumu bir kez gönderir. Gün boyu takip yoktur ve bunu burada istediğiniz zaman değiştirebilirsiniz.',
  'stats.location_yes': 'Evet, konumu gönder',
  'stats.location_no': 'Hayır, gönderme',
  'stats.location_blocked': 'Telefon konumu paylaşmadı. Lütfen tarayıcı ayarlarından izin verin.',
  'issue.title': 'Sorun bildir',
  'issue.where': 'Nerede?',
  'issue.what': 'Sorun nedir?',
  'issue.what_placeholder': 'örn. merdiven boşluğunda lamba bozuk',
  'issue.details': 'Açıklama (isteğe bağlı)',
  'issue.photo': 'Sorunun fotoğrafı',
  'issue.send': 'Bildir',
  'issue.sent': 'Teşekkürler, yönetime bilgi verildi.',
  'stats.week': 'Son 7 gün',
  'stats.month': 'Son 30 gün',
  'stats.done_of': '{total} görevden {done}',
  'stats.tasks_done': 'Tamamlanan görevler',
  'stats.private': 'Bu rakamları yalnızca siz ve yönetim görür.',
  'stats.language': 'Dil',
  'stats.appearance': 'Görünüm',
  'stats.auto_hint': '„Otomatik“ telefonunuzun ayarını izler.',
  'theme.system': 'Otomatik',
  'theme.light': 'Açık',
  'theme.dark': 'Koyu',
  'tour.title': 'Tur planlama',
  'tour.day': 'Gün',
  'tour.tasks_count': '{count} görev',
  'tour.sort': 'Sıralama',
  'tour.sort_own': 'Benim sıram',
  'tour.sort_address': 'Adrese göre',
  'tour.sort_zip': 'Posta koduna göre',
  'tour.sort_time': 'Saate göre',
  'tour.route': 'Rotayı aç ({count} durak)',
  'tour.export': 'Excel (CSV)',
  'tour.print': 'Yazdır',
  'tour.summary': '{open} açık, {done} tamamlandı',
  'tour.reorder_hint': 'Sırayı oklarla veya sürükleyerek değiştirin. Bu tarayıcıda hatırlanır.',
  'tour.col_no': 'No.',
  'tour.col_address': 'Adres',
  'tour.col_object': 'Yer',
  'tour.col_task': 'İş',
  'tour.col_time': 'Saat',
  'tour.col_note': 'Not',
  'tour.open': 'Aç',
  'tour.up': 'Yukarı',
  'tour.down': 'Aşağı',
  'tour.nothing': 'Bu gün için bir şey planlanmadı.',
  'tour.not_enabled': 'Tarayıcıda tur planlama bu hesap için açık değil. Yönetim açabilir.',
  'invite.welcome': 'Hoş geldiniz',
  'invite.hello': 'Merhaba {name}',
  'invite.enter_code': 'Lütfen size verilen 6 haneli kodu girin.',
  'invite.code': 'Kod',
  'invite.submit': 'Giriş yap',
  'invite.checking': 'Kontrol ediliyor …',
  'invite.invalid': 'Bu davet artık geçerli değil. Lütfen yönetime sorun.',
  'invite.installed_hint': 'İpucu: Sayfayı ana ekrana ekleyin, uygulama gibi açılır.',
  'error.generic': 'Bir şeyler ters gitti. Lütfen tekrar deneyin.',
  'error.offline_ok': 'İnternet yok. Sorun değil, girdiniz kaydedildi.',
  'common.cancel': 'İptal',
  'common.retry': 'Tekrar dene',
  'common.choose': 'Lütfen seçin …',
}

export const pl: Catalogue = {
  'app.title': 'Moje zadania',
  'app.tasks': 'Zadania',
  'app.stats': 'Moje liczby',
  'app.back': 'Wstecz',
  'nav.today': 'Dziś',
  'nav.plan': 'Plan',
  'nav.report': 'Zgłoś',
  'nav.stats': 'Liczby',
  'nav.tour': 'Trasa',
  'day.today': 'Dziś',
  'day.tomorrow': 'Jutro',
  'day.yesterday': 'Wczoraj',
  'plan.title': 'Najbliższe dni',
  'plan.subtitle': 'Co czeka w ciągu najbliższych dwóch tygodni',
  'plan.nothing_day': 'Na ten dzień nic nie zaplanowano.',
  'plan.nothing': 'W najbliższych dniach nic nie ma.',
  'plan.tasks_one': '1 zadanie',
  'plan.tasks_many': '{count} zadań',
  'plan.all_done': 'wszystko zrobione',
  'plan.open_count': '{count} otwarte',
  'plan.later': 'Później',
  'sync.offline': 'Brak sieci, dane są zapisywane',
  'sync.pending_one': '1 zadanie jest wysyłane',
  'sync.pending_many': 'Wysyłanie zadań: {count}',
  'sync.syncing': 'Wysyłanie …',
  'sync.done': 'Wszystko wysłane',
  'today.overdue': 'Zaległe z ostatnich dni',
  'today.today': 'Dziś',
  'today.tomorrow': 'Jutro',
  'today.done': 'Zrobione dzisiaj',
  'today.empty': 'Na dziś nic nie zaplanowano.',
  'today.all_done': 'Na dziś wszystko zrobione.',
  'today.empty_hint': 'Proszę sprawdzić później.',
  'today.loading': 'Ładowanie zadań …',
  'today.load_failed': 'Nie udało się załadować zadań. Pojawią się tutaj, gdy będzie sieć.',
  'progress.all_done': 'Wszystko zrobione. Dziękujemy!',
  'progress.remaining': 'Zostało jeszcze: {count}',
  'progress.none_left': 'Na dziś nic już nie zostało.',
  'progress.done_of': '{done} z {total} dzisiejszych zadań gotowe.',
  'card.until': 'do {time}',
  'card.photo_after': 'Zdjęcie po',
  'card.photo_both': 'Zdjęcie przed i po',
  'card.done': 'zrobione',
  'card.sending': 'wysyłanie',
  'group.open': '{count} otwarte',
  'group.finished': 'gotowe',
  'group.route': 'Trasa',
  'task.gone': 'Tego zadania nie ma już na liście.',
  'task.was_due': 'Termin był: {when}',
  'task.not_due_yet': 'To zadanie ma termin {date}. Można je już teraz odhaczyć.',
  'task.done_at': 'Zrobione {date}',
  'task.access': 'Wejście',
  'task.photo_before': 'Zdjęcie przed',
  'task.photo_after': 'Zdjęcie po',
  'task.photo_take': 'Zrób zdjęcie',
  'task.photo_retake': 'Zrób ponownie',
  'task.photo_other': 'Inne zdjęcia',
  'task.photo_optional': '(dobrowolnie)',
  'task.photo_more': 'Kolejne zdjęcie',
  'task.photo_remove': 'Usuń zdjęcie',
  'task.photo_processing': 'Zmniejszanie zdjęcia …',
  'task.photo_required_after': 'Do tego zadania potrzebne jest zdjęcie po pracy.',
  'task.photo_required_both': 'Do tego zadania potrzebne są zdjęcia przed i po.',
  'task.what_done': 'Co zostało zrobione?',
  'task.option_required': 'Proszę wybrać, co zostało zrobione.',
  'task.note': 'Notatka (opcjonalnie)',
  'task.note_placeholder': 'np. uwagi, szczególne sytuacje',
  'task.complete': 'Zrobione',
  'task.completing': 'Zapisywanie …',
  'task.completed_at': 'Zrobione {date} o {time}',
  'task.queued': 'Zapisano. Zostanie wysłane, gdy będzie sieć.',
  'task.report_issue': 'Zgłoś problem',
  'task.saved': 'Zrobione. Dziękujemy!',
  'card.team': 'Grupa {name}',
  'task.team_hint': 'Zadanie grupowe: kto pierwszy zaznaczy, ten je wykonał.',
  'task.already_done': '{name} już wykonał(a) to zadanie.',
  'task.already_done_anon': 'To zadanie jest już wykonane.',
  'task.reason_label': 'Uzasadnienie (wymagane)',
  'task.defect_label': 'Zauważona usterka? (opcjonalnie)',
  'task.defect_placeholder': 'np. gniazdo gołębi, odsłonięta elektryka, piszczy czujnik dymu',
  'task.reason_placeholder': 'Co się stało? Np. brak klucza, nikogo nie było',
  'task.reason_required': 'Proszę krótko podać powód.',
  'task.measurement_placeholder': 'Wpisz wartość',
  'task.measurement_required': 'Najpierw wpisz wartość pomiaru.',
  'task.signature': 'Podpis na miejscu',
  'task.signature_optional': 'Podpis na miejscu (opcjonalnie)',
  'task.signature_hint': 'Poproś o podpis palcem.',
  'task.signature_clear': 'Od nowa',
  'task.signed_by': 'Imię i nazwisko osoby podpisującej',
  'task.signature_required': 'To zadanie wymaga podpisu na miejscu.',
  'stats.location': 'Lokalizacja przy odhaczaniu',
  'stats.location_text': 'Twoja firma włączyła potwierdzanie lokalizacji. Jeśli się zgodzisz, aplikacja wyśle lokalizację jeden raz przy odhaczeniu zadania. Nie ma śledzenia w ciągu dnia i możesz to tutaj zmienić w każdej chwili.',
  'stats.location_yes': 'Tak, wysyłaj lokalizację',
  'stats.location_no': 'Nie, nie wysyłaj',
  'stats.location_blocked': 'Telefon nie udostępnił lokalizacji. Zezwól na to w ustawieniach przeglądarki.',
  'issue.title': 'Zgłoś problem',
  'issue.where': 'Gdzie?',
  'issue.what': 'Na czym polega problem?',
  'issue.what_placeholder': 'np. zepsuta lampa na klatce schodowej',
  'issue.details': 'Opis (opcjonalnie)',
  'issue.photo': 'Zdjęcie problemu',
  'issue.send': 'Zgłoś',
  'issue.sent': 'Dziękujemy, administracja została poinformowana.',
  'stats.week': 'Ostatnie 7 dni',
  'stats.month': 'Ostatnie 30 dni',
  'stats.done_of': '{done} z {total}',
  'stats.tasks_done': 'Zrobione zadania',
  'stats.private': 'Te liczby widzi tylko Pan/Pani i administracja.',
  'stats.language': 'Język',
  'stats.appearance': 'Wygląd',
  'stats.auto_hint': '„Automatycznie“ dostosowuje się do ustawień telefonu.',
  'theme.system': 'Automatycznie',
  'theme.light': 'Jasny',
  'theme.dark': 'Ciemny',
  'tour.title': 'Planowanie trasy',
  'tour.day': 'Dzień',
  'tour.tasks_count': 'Zadania: {count}',
  'tour.sort': 'Sortowanie',
  'tour.sort_own': 'Moja kolejność',
  'tour.sort_address': 'Według adresu',
  'tour.sort_zip': 'Według kodu pocztowego',
  'tour.sort_time': 'Według godziny',
  'tour.route': 'Otwórz trasę (przystanki: {count})',
  'tour.export': 'Excel (CSV)',
  'tour.print': 'Drukuj',
  'tour.summary': 'Otwarte: {open}, zrobione: {done}',
  'tour.reorder_hint': 'Kolejność można zmienić strzałkami lub przeciągając. Zostanie zapamiętana w tej przeglądarce.',
  'tour.col_no': 'Nr',
  'tour.col_address': 'Adres',
  'tour.col_object': 'Obiekt',
  'tour.col_task': 'Usługa',
  'tour.col_time': 'Godzina',
  'tour.col_note': 'Uwaga',
  'tour.open': 'Otwórz',
  'tour.up': 'W górę',
  'tour.down': 'W dół',
  'tour.nothing': 'Na ten dzień nic nie zaplanowano.',
  'tour.not_enabled': 'Planowanie trasy w przeglądarce nie jest włączone dla tego konta. Administracja może je włączyć.',
  'invite.welcome': 'Witamy',
  'invite.hello': 'Dzień dobry, {name}',
  'invite.enter_code': 'Proszę wpisać otrzymany 6-cyfrowy kod.',
  'invite.code': 'Kod',
  'invite.submit': 'Zaloguj',
  'invite.checking': 'Sprawdzanie …',
  'invite.invalid': 'To zaproszenie jest już nieważne. Proszę zapytać w administracji.',
  'invite.installed_hint': 'Wskazówka: dodaj stronę do ekranu głównego, wtedy otworzy się jak aplikacja.',
  'error.generic': 'Coś poszło nie tak. Proszę spróbować ponownie.',
  'error.offline_ok': 'Brak sieci. Nic się nie stało, dane są zapisane.',
  'common.cancel': 'Anuluj',
  'common.retry': 'Spróbuj ponownie',
  'common.choose': 'Proszę wybrać …',
}

export const uk: Catalogue = {
  'app.title': 'Мої завдання',
  'app.tasks': 'Завдання',
  'app.stats': 'Мої показники',
  'app.back': 'Назад',
  'nav.today': 'Сьогодні',
  'nav.plan': 'План',
  'nav.report': 'Повідомити',
  'nav.stats': 'Показники',
  'nav.tour': 'Маршрут',
  'day.today': 'Сьогодні',
  'day.tomorrow': 'Завтра',
  'day.yesterday': 'Вчора',
  'plan.title': 'Найближчі дні',
  'plan.subtitle': 'Що заплановано на наступні два тижні',
  'plan.nothing_day': 'На цей день нічого не заплановано.',
  'plan.nothing': 'Найближчими днями нічого немає.',
  'plan.tasks_one': '1 завдання',
  'plan.tasks_many': 'Завдань: {count}',
  'plan.all_done': 'усе виконано',
  'plan.open_count': 'відкрито: {count}',
  'plan.later': 'Пізніше',
  'sync.offline': 'Немає мережі, ваші дані зберігаються',
  'sync.pending_one': '1 завдання надсилається',
  'sync.pending_many': 'Надсилається завдань: {count}',
  'sync.syncing': 'Надсилання …',
  'sync.done': 'Усе надіслано',
  'today.overdue': 'Не виконано за останні дні',
  'today.today': 'Сьогодні',
  'today.tomorrow': 'Завтра',
  'today.done': 'Виконано сьогодні',
  'today.empty': 'На сьогодні нічого не заплановано.',
  'today.all_done': 'На сьогодні все виконано.',
  'today.empty_hint': 'Перевірте пізніше.',
  'today.loading': 'Завдання завантажуються …',
  'today.load_failed': 'Не вдалося завантажити завдання. Вони з’являться тут, коли буде мережа.',
  'progress.all_done': 'Усе виконано. Дякуємо!',
  'progress.remaining': 'Залишилось: {count}',
  'progress.none_left': 'На сьогодні нічого не залишилось.',
  'progress.done_of': 'Виконано {done} з {total} завдань на сьогодні.',
  'card.until': 'до {time}',
  'card.photo_after': 'Фото після',
  'card.photo_both': 'Фото до і після',
  'card.done': 'виконано',
  'card.sending': 'надсилається',
  'group.open': 'відкрито: {count}',
  'group.finished': 'готово',
  'group.route': 'Маршрут',
  'task.gone': 'Цього завдання вже немає у вашому списку.',
  'task.was_due': 'Термін був: {when}',
  'task.not_due_yet': 'Термін цього завдання {date}. Ви можете позначити його вже зараз.',
  'task.done_at': 'Виконано {date}',
  'task.access': 'Доступ',
  'task.photo_before': 'Фото до',
  'task.photo_after': 'Фото після',
  'task.photo_take': 'Зробити фото',
  'task.photo_retake': 'Зробити знову',
  'task.photo_other': 'Інші фото',
  'task.photo_optional': '(за бажанням)',
  'task.photo_more': 'Ще одне фото',
  'task.photo_remove': 'Видалити фото',
  'task.photo_processing': 'Фото зменшується …',
  'task.photo_required_after': 'Для цього завдання потрібне фото після роботи.',
  'task.photo_required_both': 'Для цього завдання потрібні фото до і після.',
  'task.what_done': 'Що було зроблено?',
  'task.option_required': 'Будь ласка, виберіть, що було зроблено.',
  'task.note': 'Примітка (необов’язково)',
  'task.note_placeholder': 'напр. особливості, зауваження',
  'task.complete': 'Виконано',
  'task.completing': 'Збереження …',
  'task.completed_at': 'Виконано {date} о {time}',
  'task.queued': 'Збережено. Буде надіслано, коли з’явиться мережа.',
  'task.report_issue': 'Повідомити про проблему',
  'task.saved': 'Виконано. Дякуємо!',
  'card.team': 'Група {name}',
  'task.team_hint': 'Групове завдання: хто першим позначить, той і виконав.',
  'task.already_done': '{name} вже виконав(ла) це завдання.',
  'task.already_done_anon': 'Це завдання вже виконано.',
  'task.reason_label': 'Пояснення (обовʼязково)',
  'task.defect_label': 'Помітили несправність? (необовʼязково)',
  'task.defect_placeholder': 'напр. гніздо голубів, відкрита проводка, пищить датчик диму',
  'task.reason_placeholder': 'Що сталося? Напр. немає ключа, нікого не було',
  'task.reason_required': 'Будь ласка, коротко поясніть причину.',
  'task.measurement_placeholder': 'Введіть значення',
  'task.measurement_required': 'Спочатку введіть виміряне значення.',
  'task.signature': 'Підпис на місці',
  'task.signature_optional': 'Підпис на місці (за бажанням)',
  'task.signature_hint': 'Попросіть розписатися пальцем.',
  'task.signature_clear': 'Заново',
  'task.signed_by': 'Ім\'я особи, яка підписує',
  'task.signature_required': 'Для цього завдання потрібен підпис на місці.',
  'stats.location': 'Місцезнаходження під час позначення',
  'stats.location_text': 'Ваша фірма увімкнула підтвердження місцезнаходження. Якщо ви погоджуєтеся, застосунок один раз надсилає місцезнаходження, коли ви позначаєте завдання виконаним. Відстеження протягом дня немає, і ви можете змінити це тут будь-коли.',
  'stats.location_yes': 'Так, надсилати місцезнаходження',
  'stats.location_no': 'Ні, не надсилати',
  'stats.location_blocked': 'Телефон не надав доступ до місцезнаходження. Дозвольте це в налаштуваннях браузера.',
  'issue.title': 'Повідомити про проблему',
  'issue.where': 'Де?',
  'issue.what': 'У чому проблема?',
  'issue.what_placeholder': 'напр. не працює лампа на сходах',
  'issue.details': 'Опис (необов’язково)',
  'issue.photo': 'Фото проблеми',
  'issue.send': 'Надіслати',
  'issue.sent': 'Дякуємо, адміністрацію повідомлено.',
  'stats.week': 'Останні 7 днів',
  'stats.month': 'Останні 30 днів',
  'stats.done_of': '{done} з {total}',
  'stats.tasks_done': 'Виконані завдання',
  'stats.private': 'Ці показники бачите лише ви та адміністрація.',
  'stats.language': 'Мова',
  'stats.appearance': 'Вигляд',
  'stats.auto_hint': '«Автоматично» відповідає налаштуванням вашого телефону.',
  'theme.system': 'Автоматично',
  'theme.light': 'Світлий',
  'theme.dark': 'Темний',
  'tour.title': 'Планування маршруту',
  'tour.day': 'День',
  'tour.tasks_count': 'Завдань: {count}',
  'tour.sort': 'Сортування',
  'tour.sort_own': 'Мій порядок',
  'tour.sort_address': 'За адресою',
  'tour.sort_zip': 'За поштовим індексом',
  'tour.sort_time': 'За часом',
  'tour.route': 'Відкрити маршрут (зупинок: {count})',
  'tour.export': 'Excel (CSV)',
  'tour.print': 'Друк',
  'tour.summary': 'Відкрито: {open}, виконано: {done}',
  'tour.reorder_hint': 'Змінюйте порядок стрілками або перетягуванням. Він запам’ятовується в цьому браузері.',
  'tour.col_no': '№',
  'tour.col_address': 'Адреса',
  'tour.col_object': 'Об’єкт',
  'tour.col_task': 'Послуга',
  'tour.col_time': 'Час',
  'tour.col_note': 'Примітка',
  'tour.open': 'Відкрити',
  'tour.up': 'Вгору',
  'tour.down': 'Вниз',
  'tour.nothing': 'На цей день нічого не заплановано.',
  'tour.not_enabled': 'Планування маршруту в браузері для цього облікового запису не ввімкнено. Адміністрація може його ввімкнути.',
  'invite.welcome': 'Ласкаво просимо',
  'invite.hello': 'Вітаємо, {name}',
  'invite.enter_code': 'Будь ласка, введіть отриманий 6-значний код.',
  'invite.code': 'Код',
  'invite.submit': 'Увійти',
  'invite.checking': 'Перевірка …',
  'invite.invalid': 'Це запрошення більше не дійсне. Зверніться до адміністрації.',
  'invite.installed_hint': 'Порада: додайте сторінку на головний екран, тоді вона відкриватиметься як застосунок.',
  'error.generic': 'Щось пішло не так. Спробуйте ще раз.',
  'error.offline_ok': 'Немає мережі. Нічого страшного, ваші дані збережено.',
  'common.cancel': 'Скасувати',
  'common.retry': 'Спробувати ще раз',
  'common.choose': 'Будь ласка, виберіть …',
}

export const ar: Catalogue = {
  'app.title': 'مهامي',
  'app.tasks': 'المهام',
  'app.stats': 'أرقامي',
  'app.back': 'رجوع',
  'nav.today': 'اليوم',
  'nav.plan': 'الخطة',
  'nav.report': 'إبلاغ',
  'nav.stats': 'الأرقام',
  'nav.tour': 'الجولة',
  'day.today': 'اليوم',
  'day.tomorrow': 'غدًا',
  'day.yesterday': 'أمس',
  'plan.title': 'الأيام القادمة',
  'plan.subtitle': 'ما هو مخطط للأسبوعين القادمين',
  'plan.nothing_day': 'لا يوجد شيء مخطط لهذا اليوم.',
  'plan.nothing': 'لا يوجد شيء في الأيام القادمة.',
  'plan.tasks_one': 'مهمة واحدة',
  'plan.tasks_many': 'المهام: {count}',
  'plan.all_done': 'تم كل شيء',
  'plan.open_count': 'مفتوحة: {count}',
  'plan.later': 'لاحقًا',
  'sync.offline': 'لا يوجد اتصال، يتم حفظ إدخالاتك',
  'sync.pending_one': 'يتم إرسال مهمة واحدة',
  'sync.pending_many': 'يتم إرسال المهام: {count}',
  'sync.syncing': 'جارٍ الإرسال …',
  'sync.done': 'تم إرسال كل شيء',
  'today.overdue': 'متبقٍ من الأيام الماضية',
  'today.today': 'اليوم',
  'today.tomorrow': 'غدًا',
  'today.done': 'ما تم اليوم',
  'today.empty': 'لا يوجد شيء مخطط لليوم.',
  'today.all_done': 'تم إنجاز كل شيء لهذا اليوم.',
  'today.empty_hint': 'يرجى التحقق لاحقًا.',
  'today.loading': 'جارٍ تحميل المهام …',
  'today.load_failed': 'تعذر تحميل المهام. ستظهر هنا عند عودة الاتصال.',
  'progress.all_done': 'تم كل شيء. شكرًا!',
  'progress.remaining': 'متبقٍ: {count}',
  'progress.none_left': 'لا يوجد شيء مفتوح لهذا اليوم.',
  'progress.done_of': 'تم إنجاز {done} من {total} من مهام اليوم.',
  'card.until': 'حتى الساعة {time}',
  'card.photo_after': 'صورة بعد',
  'card.photo_both': 'صورة قبل وبعد',
  'card.done': 'تم',
  'card.sending': 'جارٍ الإرسال',
  'group.open': 'مفتوحة: {count}',
  'group.finished': 'انتهى',
  'group.route': 'الطريق',
  'task.gone': 'هذه المهمة لم تعد في قائمتك.',
  'task.was_due': 'كان الموعد: {when}',
  'task.not_due_yet': 'موعد هذه المهمة {date}. يمكنك مع ذلك تسجيلها الآن.',
  'task.done_at': 'تم في {date}',
  'task.access': 'الدخول',
  'task.photo_before': 'صورة قبل',
  'task.photo_after': 'صورة بعد',
  'task.photo_take': 'التقاط صورة',
  'task.photo_retake': 'إعادة الالتقاط',
  'task.photo_other': 'صور أخرى',
  'task.photo_optional': '(اختياري)',
  'task.photo_more': 'صورة أخرى',
  'task.photo_remove': 'حذف الصورة',
  'task.photo_processing': 'جارٍ تصغير الصورة …',
  'task.photo_required_after': 'هذه المهمة تحتاج إلى صورة بعد العمل.',
  'task.photo_required_both': 'هذه المهمة تحتاج إلى صورة قبل وصورة بعد.',
  'task.what_done': 'ماذا تم؟',
  'task.option_required': 'يرجى اختيار ما تم.',
  'task.note': 'ملاحظة (اختياري)',
  'task.note_placeholder': 'مثلًا: ملاحظات خاصة',
  'task.complete': 'تم',
  'task.completing': 'جارٍ الحفظ …',
  'task.completed_at': 'تم في {date} الساعة {time}',
  'task.queued': 'تم الحفظ. سيُرسل عند عودة الاتصال.',
  'task.report_issue': 'الإبلاغ عن مشكلة',
  'task.saved': 'تم. شكرًا!',
  'card.team': 'المجموعة {name}',
  'task.team_hint': 'مهمة جماعية: من يؤشر عليها أولاً يُعتبر أنه أنجزها.',
  'task.already_done': 'لقد أنجز {name} هذه المهمة بالفعل.',
  'task.already_done_anon': 'هذه المهمة منجزة بالفعل.',
  'task.reason_label': 'السبب (إلزامي)',
  'task.defect_label': 'هل رأيت عيبًا؟ (اختياري)',
  'task.defect_placeholder': 'مثلًا عش حمام، أسلاك مكشوفة، كاشف دخان يصفر',
  'task.reason_placeholder': 'ماذا حدث؟ مثلاً لا يوجد مفتاح، لم يكن أحد موجوداً',
  'task.reason_required': 'يرجى كتابة السبب باختصار.',
  'task.measurement_placeholder': 'أدخل القيمة',
  'task.measurement_required': 'يرجى إدخال قيمة القياس أولاً.',
  'task.signature': 'التوقيع في الموقع',
  'task.signature_optional': 'التوقيع في الموقع (اختياري)',
  'task.signature_hint': 'اطلب التوقيع بالإصبع.',
  'task.signature_clear': 'من جديد',
  'task.signed_by': 'اسم الشخص الموقّع',
  'task.signature_required': 'تحتاج هذه المهمة إلى توقيع في الموقع.',
  'stats.location': 'الموقع عند الإنجاز',
  'stats.location_text': 'قامت شركتك بتفعيل إثبات الموقع. إذا وافقت، يرسل التطبيق الموقع مرة واحدة عند تأشير المهمة كمنجزة. لا يوجد تتبع خلال اليوم، ويمكنك تغيير ذلك هنا في أي وقت.',
  'stats.location_yes': 'نعم، أرسل الموقع',
  'stats.location_no': 'لا، لا ترسل',
  'stats.location_blocked': 'لم يسمح الهاتف بمشاركة الموقع. يرجى السماح بذلك في إعدادات المتصفح.',
  'issue.title': 'الإبلاغ عن مشكلة',
  'issue.where': 'أين؟',
  'issue.what': 'ما المشكلة؟',
  'issue.what_placeholder': 'مثلًا: مصباح الدرج معطل',
  'issue.details': 'الوصف (اختياري)',
  'issue.photo': 'صورة المشكلة',
  'issue.send': 'إبلاغ',
  'issue.sent': 'شكرًا، تم إبلاغ الإدارة.',
  'stats.week': 'آخر 7 أيام',
  'stats.month': 'آخر 30 يومًا',
  'stats.done_of': '{done} من {total}',
  'stats.tasks_done': 'المهام المنجزة',
  'stats.private': 'لا يرى هذه الأرقام إلا أنت والإدارة.',
  'stats.language': 'اللغة',
  'stats.appearance': 'المظهر',
  'stats.auto_hint': '«تلقائي» يتبع إعدادات هاتفك.',
  'theme.system': 'تلقائي',
  'theme.light': 'فاتح',
  'theme.dark': 'داكن',
  'tour.title': 'تخطيط الجولة',
  'tour.day': 'اليوم',
  'tour.tasks_count': 'المهام: {count}',
  'tour.sort': 'الترتيب',
  'tour.sort_own': 'ترتيبي',
  'tour.sort_address': 'حسب العنوان',
  'tour.sort_zip': 'حسب الرمز البريدي',
  'tour.sort_time': 'حسب الوقت',
  'tour.route': 'فتح الطريق (المحطات: {count})',
  'tour.export': 'Excel (CSV)',
  'tour.print': 'طباعة',
  'tour.summary': 'مفتوحة: {open}، منجزة: {done}',
  'tour.reorder_hint': 'غيّر الترتيب بالأسهم أو بالسحب. يُحفظ في هذا المتصفح.',
  'tour.col_no': 'رقم',
  'tour.col_address': 'العنوان',
  'tour.col_object': 'الموقع',
  'tour.col_task': 'الخدمة',
  'tour.col_time': 'الوقت',
  'tour.col_note': 'ملاحظة',
  'tour.open': 'فتح',
  'tour.up': 'إلى الأعلى',
  'tour.down': 'إلى الأسفل',
  'tour.nothing': 'لا يوجد شيء مخطط لهذا اليوم.',
  'tour.not_enabled': 'تخطيط الجولة في المتصفح غير مفعّل لهذا الحساب. يمكن للإدارة تفعيله.',
  'invite.welcome': 'أهلًا بك',
  'invite.hello': 'مرحبًا {name}',
  'invite.enter_code': 'يرجى إدخال الرمز المكوّن من 6 أرقام الذي استلمته.',
  'invite.code': 'الرمز',
  'invite.submit': 'تسجيل الدخول',
  'invite.checking': 'جارٍ التحقق …',
  'invite.invalid': 'هذه الدعوة لم تعد صالحة. يرجى السؤال لدى الإدارة.',
  'invite.installed_hint': 'نصيحة: أضف الصفحة إلى الشاشة الرئيسية لتفتح مثل التطبيق.',
  'error.generic': 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
  'error.offline_ok': 'لا يوجد اتصال. لا بأس، تم حفظ إدخالك.',
  'common.cancel': 'إلغاء',
  'common.retry': 'إعادة المحاولة',
  'common.choose': 'يرجى الاختيار …',
}
